/* eslint-disable */
import * as React from "react"
import { CircularProgress, Switch, SwitchProps } from "@mui/material"
import Pagination from "../../../components/TablePagination"
import { SnackbarOrigin } from "@mui/material/Snackbar"
import TableCell, { tableCellClasses } from "@mui/material/TableCell"
import { styled } from "@mui/material/styles"
import Box from "@mui/material/Box"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableSortLabel from "@mui/material/TableSortLabel"
import Paper from "@mui/material/Paper"
import { fetchEmailGroups, toggleEmailGroupEnabled } from "../redux/actionCreators"
import { visuallyHidden } from "@mui/utils"
import { type RootState } from "../../../store"
import { EmailGroupsListState } from "../redux/emailGroupsListSlice"
import { type ConnectedProps, connect } from "react-redux"
import { type OperationPermissionState } from "../../dashboard/redux/operationPermissionSlice"
import { useSelector } from "react-redux"
import { useAuth } from "react-oidc-context"
import { getOperationPermissions } from "../../dashboard/redux/actionCreator"
import LanguageStrings from "../../../i18n/locales"
import AuthError from "../../../components/ErrorComponents/AuthError"
import { APIConfiguration } from "../../../utils/apiConfiguration"
import ActionComponent from "./ActionComponent"
import CustomToast from "./ToastComponent"

const authErrors = LanguageStrings().AuthErrors
const emailNotificationStrings = LanguageStrings().ThermalIQStrings.EmailNotificationComponent
const httpStatus = APIConfiguration

export interface State extends SnackbarOrigin {
  open: boolean
}

interface Data {
  EnableDisable: string
}

export type Order = "asc" | "desc"
interface HeadCell {
  disablePadding: boolean
  id: any
  label: string
  numeric: boolean
  sortable: boolean
}

const headCells: readonly HeadCell[] = [
  {
    id: "Group Name",
    numeric: false,
    disablePadding: false,
    label: emailNotificationStrings.GroupName,
    sortable: false
  },
  {
    id: "EnableDisable",
    numeric: false,
    disablePadding: false,
    label: emailNotificationStrings.EnableDisable,
    sortable: true
  },
  {
    id: "Actions",
    numeric: false,
    disablePadding: false,
    label: emailNotificationStrings.Action,
    sortable: false
  }
]

function EnhancedTableHead(props: any): any {
  const { order, orderBy, onRequestSort, emailGroupList } = props
  const createSortHandler =
    (newOrderBy: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy)
    }
  // istanbul ignore else
  if (emailGroupList !== null && emailGroupList !== undefined) {
    return (
      <TableHead data-testid="table-header">
        <TableRow>
          {headCells.map((headCell: any) => (
            <StyledTableCell
              key={headCell.id}
              sx={{
                fontFamily: "Honeywell Sans Web",
                fontSize: "12px",
                fontWeight: 700,
                lineHeight: "16px",
                letterSpacing: "2px",
              }}
              align={headCell.numeric ? "right" : "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              <TableSortLabel
                sx={{
                  "& .MuiTableSortLabel-icon": {
                    opacity: 1,
                    marginLeft: "100px",
                  },
                }}
                hideSortIcon={!headCell.sortable}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={
                  headCell.sortable ? createSortHandler(headCell.id) : undefined
                }
                aria-label={headCell.label}
                data-testid="test-sort"
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            </StyledTableCell>
          ))}
        </TableRow>
      </TableHead>
    )
  }
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: "#F0F0F0",
    fontWeight: "700",
    fontSize: 12,
    border: "2px solid #202020",
    padding: "5px 16px",
    fontFamily: "Honeywell Sans Web",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: "#d0d0d0",
    paddingLeft: "17px",
    borderBottom: "1px solid #202020",
    borderRight: 0,
    fontFamily: "Honeywell Sans Web",
  },
}))

export interface Sort {
  order: Order
  orderBy: string
}

function EmailList(props: AllProps) {
  const [selectedEmailGroup, setSelectedEmailGroup] = React.useState(null)
  const [toggleLoading, setToggleLoading] = React.useState<string | null>(null)
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const auth = useAuth()

  React.useEffect(() => {
    fetchAllEmailGroups()
  }, [props.page, props.sort])

  React.useEffect(() => {
    (!operationPermissions.updated && props.getOperationPermissions(auth.user?.profile.sub as string))
  }, [])

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>, row: any) => {
    setToggleLoading(row.groupId)
    props.toggleEmailGroupEnabled(
      row.groupId,
      event.target.checked,
      () => {
        setToggleLoading(null)
        CustomToast({
          type: 'success',
          heading: 'Success',
          message: 'Group status updated successfully'
        })
        fetchAllEmailGroups()
      },
      (errorMessage) => {
        setToggleLoading(null)
        CustomToast({
          type: 'error',
          heading: 'Error',
          message: errorMessage || "Failed to update group status"
        })
      }
    )
  }

  const fetchAllEmailGroups = () => {
    // istanbul ignore else
    if (props.page >= 1) {
      // Set sortEnabled based on both orderBy and order
      let sortEnabled = true
      if (props.sort.orderBy === "EnableDisable") {
        sortEnabled = props.sort.order === "asc"
      }
      props.fetchEmailGroups(
        props.page - 1,
        props.rowsPerPage,
        sortEnabled
      )
    }
  }

  const handleGroupSelect = (): void => {
    setSelectedEmailGroup(null)
  }

  const CustomSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
  ))(({ theme }) => ({
    width: 48,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: '5px 9px',
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(16px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: theme.palette.mode === 'dark' ? '#B0B0B0' : '#64C3FF',
          opacity: 1,
          border: 0
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5
        },
        '& .MuiSwitch-thumb': {
          color: '#000'
        }
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: '#33cf4d',
        border: '6px solid #fff'
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        color: '#707070'
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: theme.palette.mode === 'light' ? 0.7 : 0.3
      }
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 15,
      height: 15
    },
    '& .MuiSwitch-track': {
      borderRadius: 26 / 2,
      backgroundColor: theme.palette.mode === 'light' ? '#B0B0B0' : '#64C3FF',
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500
      })
    }
  }))

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    newOrderBy: keyof Data
  ) => {
    let toggledOrder: Order = "desc"
    if (props.sort.orderBy === newOrderBy) {
      toggledOrder = props.sort.order === "desc" ? "asc" : "desc"
    } else if (newOrderBy === "EnableDisable") {
      toggledOrder = "asc"
    }
    props.setSort({ order: toggledOrder, orderBy: newOrderBy })
  }

  // istanbul ignore else
  if (
    props.emailGroupsList.emailGroups !== null &&
    props.emailGroupsList.emailGroups !== undefined &&
    props.emailGroupsList.httpStatus === httpStatus.SUCCESS_HTTP_STATUS &&
    props.emailGroupsList.totalCount > 0
  ) {
    return (
      <>
        <Box className="devices">
          <Paper
            sx={{
              width: "100%",
              mb: 2,
              backgroundColor: "#272727",
              boxShadow: "unset",
              color: "#F0F0F0",
              marginBottom: "0",
            }}
          >
            <TableContainer className="device-table">
              <Table
                sx={{ minWidth: 750, color: "#f0f0f0" }}
                aria-labelledby="tableTitle"
                size={"small"}
              >
                <EnhancedTableHead
                  order={props.sort.order}
                  orderBy={props.sort.orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={props.emailGroupsList.emailGroups.length}
                  emailGroupList={props.emailGroupsList.emailGroups}
                />
                <TableBody>
                  {props.emailGroupsList.emailGroups &&
                    props.emailGroupsList.emailGroups.map((row: any, index: any) => {
                      return (
                        <TableRow key={index}>
                          <StyledTableCell style={{ width: "60%" }}>
                            {row.groupName}
                          </StyledTableCell>
                          <StyledTableCell style={{ width: "20%" }}>
                            {toggleLoading === row.groupId ? (
                              <CircularProgress size={24} />
                            ) : (
                              <CustomSwitch sx={{ m: 1 }} checked={row.enabled}
                                size="medium"
                                data-testid={`select-switch`}
                                disabled={toggleLoading !== null || !operationPermissions.permissions.can_modify_tiq_communications_service}
                                onChange={e => handleToggleChange(e, row)}
                              />
                            )}
                          </StyledTableCell>
                          <StyledTableCell style={{ width: "25%"}}>
                            <ActionComponent
                              row={row}
                              fetchAllGroups={fetchAllEmailGroups}
                              isGroupSelected={selectedEmailGroup === row.groupId}
                              setIsGroupSelected={handleGroupSelect}
                            />
                          </StyledTableCell>
                        </TableRow>
                      )
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={props.emailGroupsList.totalCount}
              page={props.page}
              setPage={props.setPage}
              rowsPerPage={props.rowsPerPage}
              siblingCount={1}
              boundaryCount={1}
            />
          </Paper>
        </Box>
      </>
    )
  } else if (props.emailGroupsList.isLoading) {
    return (
      <div className="CircularprogressAmi" data-testid="loading-spinner">
        <CircularProgress />
      </div>
    )
  } else if (props.emailGroupsList.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS) {
    return (
      <div className="authError">
        <AuthError errorMessage={authErrors.Unauthorized} />
      </div>
    )
  } else if (
    props.emailGroupsList.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage={authErrors.AccessForbidden} />
      </div>
    )
  } else if (
    props.emailGroupsList.emailGroups !== null &&
    props.emailGroupsList.emailGroups !== undefined &&
    props.emailGroupsList.emailGroups.length === 0
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage={authErrors.NoDataPresent} />
      </div>
    )
  } else {
    return (
      <div className="authError">
        <AuthError
          errorMessage={authErrors.CanNotFetch}
          retry={fetchAllEmailGroups}
        />
      </div>
    )
  }
}

interface DispatchToProps {
  fetchEmailGroups: (
    page: number,
    size: number,
    sortEnabled: boolean
  ) => void
  toggleEmailGroupEnabled: (
    groupId: string,
    enabled: boolean,
    onSuccess: () => void,
    onFailure: (errorMessage: string) => void
  ) => void
  getOperationPermissions: (sub: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchEmailGroups: (
    page: number,
    size: number,
    sortEnabled: boolean
  ) => dispatch(fetchEmailGroups(page, size, sortEnabled)),
  toggleEmailGroupEnabled: (
    groupId: string,
    enabled: boolean,
    onSuccess: () => void,
    onFailure: (errorMessage: string) => void
  ) => dispatch(toggleEmailGroupEnabled(groupId, enabled, onSuccess, onFailure)),
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub))
})

interface StateToProps {
  emailGroupsList: EmailGroupsListState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  emailGroupsList: state.emailGroupsList
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>
interface ParentProps {
  rowsPerPage: number
  page: number
  sort: Sort
  setPage: React.Dispatch<React.SetStateAction<number>>
  setSort: React.Dispatch<React.SetStateAction<Sort>>
  orderBy: string
}
type AllProps = PropsFromRedux & ParentProps

export default connector(EmailList)
